import { inject, Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, lastValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ROLE_ADMIN, ROLE_DISTRIBUTOR, ROLE_MANAGER, ROLE_TECH, UserDataType, UserModel } from '../models/user.model';
import { AuthHTTPService } from './auth-http';
import { Router } from '@angular/router';
import { OptionalHeaders } from './auth-http/auth-http.service';
import {
  Auth,
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  fetchSignInMethodsForEmail 
} from '@angular/fire/auth';
import { browserLocalPersistence, setPersistence } from 'firebase/auth';
import { authLocalStorageToken, getStorageUserData } from 'src/app/services/shared.service';
import { MESSAGE_TYPE_LOGOUT } from 'src/app/models/message.model';


@Injectable({
    providedIn: 'root',
})
export class AuthService {

    private auth = inject(Auth);
 
    isLoading$: Observable<boolean>;
    isLoadingSubject: BehaviorSubject<boolean>;

    currentUserSubject: BehaviorSubject<User | null>;

    getCurrentUser(): User | null {
        return this.auth.currentUser;
    }
    
    constructor(
        private authHttpService: AuthHTTPService,
        private router: Router,
    ) {
        this.isLoadingSubject = new BehaviorSubject<boolean>(false);
        this.currentUserSubject = new BehaviorSubject<User | null>(null);
        this.isLoading$ = this.isLoadingSubject.asObservable();
       
        // Configura persistencia local (muy importante en WebView)
        setPersistence(this.auth, browserLocalPersistence).then(() => {
            // Escucha cambios de sesión
            onAuthStateChanged(this.auth, (user) => {
                this.currentUserSubject.next(user);
            });
        })
        .catch(err => {
            console.error('Error setting persistence:', err);
        });         
    }

    async getToken(): Promise<string | null> {
        const user = this.auth.currentUser;
        if (!user) return null;
        return await user.getIdToken();
    }

    async loginv2(email: string, password: string): Promise<User | null> {
        const credential = await signInWithEmailAndPassword(this.auth, email, password);
        return credential.user;
    }

    async signInWithCustomToken(token: string): Promise<User | null> {    
        const credential = await signInWithCustomToken(this.auth, token); 
        this.currentUserSubject.next(credential.user); 
        return credential.user;  
    }
   
    async getUserData(user: User, optHeaders?: OptionalHeaders[]) {    
        const tokenResult = await user.getIdTokenResult(true);         
        const role = tokenResult?.claims.role;
        let userData: UserDataType = undefined;
        if(role == ROLE_ADMIN || role == ROLE_MANAGER) {            
            userData = {
                uid: user.uid,
                email: user.email || '',
                displayName: user.displayName || '',
                photoURL: user.photoURL || '',
                role,
            };  
        }
        else {            
            const token = user ? await user.getIdToken() : null;
            if(token) {
                const profileResponse = await lastValueFrom(this.authHttpService.getUserByToken(optHeaders));
                if(profileResponse) {                   
                    userData = {
                        uid: user.uid,
                        email: user.email || '',
                        displayName: user.displayName || '',
                        photoURL: user.photoURL || '',
                        club: tokenResult?.claims.club,
                        role,
                        permissions: tokenResult?.claims.permissions,
                        teams: profileResponse.user.profile?.teams || [],
                    };  
                }
            }            
        }
        return userData;          
    }

    /*async getValidToken(userData: any): Promise<string> {
        if(userData.auth) {
            const expiration = new Date(userData.auth.expirationTime).getTime();
            const now = Date.now();
            const fiveMinutes = 5 * 60 * 1000;

            if (expiration - now < fiveMinutes) {
                const token = await user.getIdToken(true); // Fuerza renovación
            }
        }
    }*/
   

    async logout(returnUrl?: string): Promise<void> {
        const user = this.getCurrentUser();
        return this.auth.signOut().then(() => {   
            localStorage.removeItem(user?.uid + '_tech');
            this.currentUserSubject.next(null);
            window.parent.postMessage({
                type: MESSAGE_TYPE_LOGOUT,
                data: {}
            }, '*');

            this.router.navigate(['/auth/login'], {
                queryParams: { returnUrl },
            });
        });
    }

    registration(user: UserModel): Observable<any> {
        return of(undefined);
    }

    forgotPassword(email: string): Observable<boolean> {
        this.isLoadingSubject.next(true);
        return this.authHttpService
          .forgotPassword(email)
          .pipe(finalize(() => this.isLoadingSubject.next(false)));
    }      

    public storageUserData(user: UserDataType): boolean {            
        if (user && user.uid) {             
            localStorage.setItem(authLocalStorageToken, JSON.stringify(user));
            return true;
        }
        else {
            localStorage.removeItem(authLocalStorageToken);
            return false;
        }
    }

    public hasPermission(permission: string): boolean {
        let userData = getStorageUserData();
        if(this.isAdmin()) return true;
        else if(userData && userData.permissions) {
            return userData.permissions.includes(permission);
        }
        return false;
    }

    public isSysAdmin(): boolean {
        let userData = getStorageUserData();
        if(userData) {
            return userData.role == ROLE_ADMIN;
        }
        return false;
    }

    public isAdmin(): boolean {
        let userData = getStorageUserData();
        if(userData) {
            return userData.role == ROLE_ADMIN || userData.role == ROLE_DISTRIBUTOR;
        }
        return false;
    }

    public isTech(): boolean {
        let userData = getStorageUserData();
        if(userData) {
            return userData.role == ROLE_TECH;
        }
        return false;
    }

    public isManager(): boolean {
        let userData = getStorageUserData();
        if(userData) {
            return userData.role == ROLE_MANAGER;
        }
        return false;
    }

    public isRole(role: string): boolean {
        switch (role) {
            case ROLE_ADMIN:
                return this.isAdmin();     
            case ROLE_TECH:
                return this.isTech();  
            case ROLE_MANAGER:
                return this.isManager();      
            default:
                return true;;
        }
    }

    public getUid(): any {
        let userData = getStorageUserData();
        if(userData) {
            return userData.uid;
        }
        return undefined;
    }  
    
    async checkIfEmailExists(email: string): Promise<boolean> {
        return fetchSignInMethodsForEmail(this.auth, email)
        .then((signInMethods) => {
            return signInMethods.length > 0;
        })
        .catch((error) => {
            console.error("Error checking email:", error);
            return false;
        });
    }
}